import { useEffect, useMemo, useState } from 'react';
import { AlertCircle, Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Textarea } from '@/app/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import { Badge } from '@/app/components/ui/badge';
import { handleApiError } from '@/lib/api-client';
import {
  useCreateTravelItinerary,
  useCreateTravelSegment,
  useDeleteTravelSegment,
  useUpdateTravelItinerary,
  useUpdateTravelSegment,
} from '@/hooks/use-crew';
import type {
  TravelItinerary,
  TravelSegment,
  TravelSegmentType,
} from '@/lib/types';
import { toIsoFromDatetimeLocal } from '@/lib/date-utils';
import { toast } from '@/app/components/ui/simple-toaster';

const SEGMENT_OPTIONS: Array<{ value: TravelSegmentType; label: string }> = [
  { value: 'flight', label: 'Flight' },
  { value: 'lodging', label: 'Lodging' },
  { value: 'ground', label: 'Ground' },
  { value: 'other', label: 'Other' },
];

function toLocalInput(value: string | null | undefined) {
  if (!value) return '';
  const d = new Date(value);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function sortSegments(segments: TravelSegment[]) {
  return [...segments].sort((a, b) => a.order - b.order || a.starts_at.localeCompare(b.starts_at));
}

export function TravelItineraryEditor({
  itinerary,
  assignmentId,
}: {
  itinerary: TravelItinerary | null;
  assignmentId: number;
}) {
  const createItinerary = useCreateTravelItinerary();
  const updateItinerary = useUpdateTravelItinerary();
  const createSegment = useCreateTravelSegment();
  const updateSegment = useUpdateTravelSegment();
  const deleteSegment = useDeleteTravelSegment();

  const [notes, setNotes] = useState('');
  const [itineraryError, setItineraryError] = useState<string | null>(null);
  const [segmentError, setSegmentError] = useState<string | null>(null);
  const [editingSegmentId, setEditingSegmentId] = useState<number | null>(null);
  const [segmentType, setSegmentType] = useState<TravelSegmentType>('flight');
  const [title, setTitle] = useState('');
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [confirmationCode, setConfirmationCode] = useState('');
  const [details, setDetails] = useState('');
  const [segmentNotes, setSegmentNotes] = useState('');
  const [order, setOrder] = useState('0');

  useEffect(() => {
    setNotes(itinerary?.notes ?? '');
  }, [itinerary?.notes]);

  const sortedSegments = useMemo(() => sortSegments(itinerary?.segments ?? []), [itinerary?.segments]);

  useEffect(() => {
    if (!itinerary) {
      setEditingSegmentId(null);
      return;
    }

    const fallbackOrder = String(sortedSegments.length + 1);
    setOrder(fallbackOrder);

    if (editingSegmentId === null) return;
    const current = sortedSegments.find((segment) => segment.id === editingSegmentId);
    if (!current) {
      setEditingSegmentId(null);
      return;
    }

    setSegmentType(current.segment_type);
    setTitle(current.title);
    setStartsAt(toLocalInput(current.starts_at));
    setEndsAt(toLocalInput(current.ends_at));
    setConfirmationCode(current.confirmation_code);
    setDetails(current.details);
    setSegmentNotes(current.notes);
    setOrder(String(current.order));
  }, [editingSegmentId, itinerary, sortedSegments]);

  useEffect(() => {
    if (!itinerary) return;
    setNotes(itinerary.notes);
  }, [itinerary?.id]);

  const resetSegmentForm = () => {
    setEditingSegmentId(null);
    setSegmentType('flight');
    setTitle('');
    setStartsAt('');
    setEndsAt('');
    setConfirmationCode('');
    setDetails('');
    setSegmentNotes('');
    setOrder(String(sortedSegments.length + 1));
    setSegmentError(null);
  };

  const saveItineraryNotes = async () => {
    if (!itinerary) return;
    try {
      await updateItinerary.mutateAsync({ id: itinerary.id, data: { notes } });
      toast.success('Itinerary notes saved.');
    } catch (err) {
      setItineraryError(handleApiError(err).message);
    }
  };

  const createItineraryHandler = async () => {
    try {
      await createItinerary.mutateAsync({ assignment: assignmentId, notes });
      toast.success('Travel itinerary created.');
    } catch (err) {
      setItineraryError(handleApiError(err).message);
    }
  };

  const saveSegment = async () => {
    if (!itinerary) return;
    if (!title || !startsAt) {
      setSegmentError('Title and start time are required.');
      return;
    }

    const payload = {
      segment_type: segmentType,
      title,
      starts_at: toIsoFromDatetimeLocal(startsAt),
      ends_at: endsAt ? toIsoFromDatetimeLocal(endsAt) : null,
      confirmation_code: confirmationCode || undefined,
      details: details || undefined,
      notes: segmentNotes || undefined,
      order: Number(order) || 0,
    };

    try {
      if (editingSegmentId) {
        await updateSegment.mutateAsync({ id: editingSegmentId, data: payload });
        toast.success('Segment updated.');
      } else {
        await createSegment.mutateAsync({ itinerary: itinerary.id, ...payload });
        toast.success('Segment added.');
      }
      resetSegmentForm();
    } catch (err) {
      setSegmentError(handleApiError(err).message);
    }
  };

  const startEditing = (segment: TravelSegment) => {
    setEditingSegmentId(segment.id);
    setSegmentType(segment.segment_type);
    setTitle(segment.title);
    setStartsAt(toLocalInput(segment.starts_at));
    setEndsAt(toLocalInput(segment.ends_at));
    setConfirmationCode(segment.confirmation_code);
    setDetails(segment.details);
    setSegmentNotes(segment.notes);
    setOrder(String(segment.order));
    setSegmentError(null);
  };

  const removeSegment = async (segment: TravelSegment) => {
    if (!itinerary) return;
    const ok = window.confirm('Delete this segment? The itinerary will need to be reconfirmed.');
    if (!ok) return;

    try {
      await deleteSegment.mutateAsync({ id: segment.id, itineraryId: itinerary.id });
      toast.success('Segment deleted.');
      if (editingSegmentId === segment.id) resetSegmentForm();
    } catch (err) {
      setSegmentError(handleApiError(err).message);
    }
  };

  if (!itinerary) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Create travel itinerary</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="itinerary-notes">Itinerary notes</Label>
            <Textarea
              id="itinerary-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes for the tech"
              rows={4}
            />
          </div>
          {itineraryError && (
            <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
              {itineraryError}
            </div>
          )}
          <Button onClick={createItineraryHandler} disabled={createItinerary.isPending}>
            {createItinerary.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
            Create itinerary
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Pencil className="h-4 w-4 text-slate-500" />
            Itinerary notes
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={4}
            placeholder="Optional notes for the tech"
          />
          {itineraryError && (
            <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
              {itineraryError}
            </div>
          )}
          <Button onClick={saveItineraryNotes} disabled={updateItinerary.isPending}>
            {updateItinerary.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save notes
          </Button>
        </CardContent>
      </Card>

      <Card className="border-amber-200 bg-amber-50/60">
        <CardContent className="flex items-start gap-3 p-4 text-sm text-amber-900">
          <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <p>
            Editing, adding, or removing a segment clears the tech&apos;s confirmation so they must
            re-confirm the itinerary.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{editingSegmentId ? 'Edit segment' : 'Add segment'}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {segmentError && (
            <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
              {segmentError}
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label>Type</Label>
              <Select value={segmentType} onValueChange={(value) => setSegmentType(value as TravelSegmentType)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SEGMENT_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Order</Label>
              <Input value={order} onChange={(e) => setOrder(e.target.value)} type="number" min="0" />
            </div>
          </div>

          <div>
            <Label>Title</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="United UA123"
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label>Starts at</Label>
              <Input
                value={startsAt}
                onChange={(e) => setStartsAt(e.target.value)}
                type="datetime-local"
              />
            </div>
            <div>
              <Label>Ends at</Label>
              <Input
                value={endsAt}
                onChange={(e) => setEndsAt(e.target.value)}
                type="datetime-local"
              />
            </div>
          </div>

          <div>
            <Label>Confirmation code</Label>
            <Input
              value={confirmationCode}
              onChange={(e) => setConfirmationCode(e.target.value)}
              placeholder="ABC123"
            />
          </div>

          <div>
            <Label>Details</Label>
            <Textarea
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              rows={3}
              placeholder="Gate, hotel address, pickup details..."
            />
          </div>

          <div>
            <Label>Notes</Label>
            <Textarea
              value={segmentNotes}
              onChange={(e) => setSegmentNotes(e.target.value)}
              rows={3}
              placeholder="Internal notes for the crew"
            />
          </div>

          <div className="flex flex-wrap gap-2">
            <Button onClick={saveSegment} disabled={createSegment.isPending || updateSegment.isPending}>
              {(createSegment.isPending || updateSegment.isPending) ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Plus className="mr-2 h-4 w-4" />
              )}
              {editingSegmentId ? 'Save changes' : 'Add segment'}
            </Button>
            {editingSegmentId && (
              <Button variant="outline" onClick={resetSegmentForm}>
                Cancel
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Badge className="bg-slate-100 text-slate-700">{sortedSegments.length}</Badge>
            Segments
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {sortedSegments.length === 0 ? (
            <p className="text-sm text-slate-500">No segments yet.</p>
          ) : (
            sortedSegments.map((segment) => (
              <div
                key={segment.id}
                className="flex flex-col gap-3 rounded-xl border bg-white p-4 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0">
                  <p className="font-medium text-slate-900">{segment.title}</p>
                  <p className="text-sm text-slate-500">
                    {segment.segment_type} · {segment.starts_at}
                  </p>
                  {segment.confirmation_code && (
                    <p className="mt-1 text-xs text-slate-500">
                      Conf: <span className="font-mono">{segment.confirmation_code}</span>
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => startEditing(segment)}>
                    <Pencil className="mr-2 h-4 w-4" />
                    Edit
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => removeSegment(segment)}>
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                  </Button>
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}
